import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 Minuten
const WARNING_BEFORE = 5 * 60 * 1000;      // Warnung 5 Min vorher

/**
 * Auto-Logout nach 30 Minuten Inaktivität.
 * Trackt mousemove, keydown, mousedown, scroll, touchstart.
 * Zeigt 5 Min vor Ablauf eine Warnung.
 * Synct Cross-Tab über localStorage.
 */
export default function useInactivityLogout() {
  const navigate = useNavigate();
  const logoutTimer = useRef(null);
  const warningTimer = useRef(null);
  const warningShown = useRef(false);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.setItem('inactivity_logout', Date.now().toString());
    toast.info('Automatisch abgemeldet wegen Inaktivität.');
    navigate('/login?reason=inactivity');
  }, [navigate]);

  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (logoutTimer.current) clearTimeout(logoutTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
    warningShown.current = false;

    // Warning timer
    warningTimer.current = setTimeout(() => {
      warningShown.current = true;
      toast.warning('Ihre Sitzung läuft in 5 Minuten ab. Bewegen Sie die Maus, um angemeldet zu bleiben.', {
        duration: 10000,
      });
    }, INACTIVITY_TIMEOUT - WARNING_BEFORE);

    // Logout timer
    logoutTimer.current = setTimeout(logout, INACTIVITY_TIMEOUT);

    // Persist last activity for cross-tab awareness
    localStorage.setItem('last_activity', Date.now().toString());
  }, [logout]);

  useEffect(() => {
    // Only run if user is logged in
    const token = localStorage.getItem('token');
    if (!token) return;

    const activityEvents = ['mousemove', 'keydown', 'mousedown', 'scroll', 'touchstart'];

    // Throttle: max 1 reset per second
    let lastReset = 0;
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastReset < 1000) return;
      lastReset = now;
      resetTimers();
    };

    // Cross-tab sync: if another tab logs out, follow
    const handleStorage = (e) => {
      if (e.key === 'inactivity_logout') {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        navigate('/login?reason=inactivity');
      }
      // If token removed externally (e.g. by apiClient 401 handler)
      if (e.key === 'token' && !e.newValue) {
        navigate('/login');
      }
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    window.addEventListener('storage', handleStorage);

    // Start initial timers
    resetTimers();

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('storage', handleStorage);
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
      if (warningTimer.current) clearTimeout(warningTimer.current);
    };
  }, [navigate, resetTimers]);
}
